import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Link,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  getFeedbackPaginated,
  updateFeedback,
  deleteFeedback,
  listOfAnalysts,
  fetchKeywords,
} from "../services/NewsService";

const UserFeedbackTable = () => {
  const theme = useTheme();

  // State management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [feedbackFilter, setFeedbackFilter] = useState("");
  const [analystFilter, setAnalystFilter] = useState("");
  const [analysts, setAnalysts] = useState([]);
  const [analystsLoading, setAnalystsLoading] = useState(false);
  const [keywordsData, setKeywordsData] = useState([]); // Store keywords to get user assignments

  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  // Get unique tags for filter dropdown
  const uniqueTags = useMemo(() => {
    const tags = [...new Set(data.map((item) => item.tag))].sort();
    return tags;
  }, [data]);

  // Load analysts list
  useEffect(() => {
    const loadAnalysts = async () => {
      setAnalystsLoading(true);
      try {
        const data = await listOfAnalysts();
        const analystsList = data.analysts || [];
        setAnalysts(analystsList);
      } catch (err) {
        console.error("Error loading analysts:", err);
      } finally {
        setAnalystsLoading(false);
      }
    };

    loadAnalysts();
  }, []);

  // Load keywords data to get user assignments for filtering
  useEffect(() => {
    const loadKeywords = async () => {
      try {
        const data = await fetchKeywords();
        setKeywordsData(data || []);
      } catch (err) {
        console.error("Error loading keywords:", err);
      }
    };

    loadKeywords();
  }, []);

  // Load data
  const loadData = async (pageNum = page, pageSize = rowsPerPage) => {
    try {
      setLoading(true);
      setError(null);

      console.log("Loading feedback data with params:", {
        page: pageNum + 1,
        pageSize,
        searchTerm,
        tagFilter,
        feedbackFilter,
      });

      // Load all data without analyst filter (will filter on frontend)
      const response = await getFeedbackPaginated(
        pageNum + 1, // API uses 1-based pagination
        pageSize,
        searchTerm || null,
        tagFilter || null,
        feedbackFilter === "" ? null : feedbackFilter === "true",
        null // No analyst filter on backend
      );

      console.log("Feedback data response:", response);

      // Apply analyst filter on frontend if needed
      let filteredData = response.data;
      if (analystFilter) {
        const analystName =
          typeof analystFilter === "object"
            ? analystFilter.name
            : analystFilter;

        // Get all tags where users field contains this analyst
        const matchingTags = keywordsData
          .filter((keyword) => {
            const users = keyword.users || "";
            return users.toLowerCase().includes(analystName.toLowerCase());
          })
          .map((keyword) => keyword.tag);

        // Filter feedback data to only include items with matching tags
        if (matchingTags.length > 0) {
          filteredData = response.data.filter((item) =>
            matchingTags.includes(item.tag)
          );
        } else {
          filteredData = [];
        }
      }

      setData(filteredData);
      // Update pagination counts based on filtered data
      const filteredTotal = analystFilter
        ? filteredData.length
        : response.pagination.total_count;
      setTotalCount(filteredTotal);
      setTotalPages(Math.ceil(filteredTotal / pageSize));
    } catch (err) {
      console.error("Detailed error loading feedback data:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setError(`Error: ${err.message} (Status: ${err.response?.status})`);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    loadData();
  }, [page, rowsPerPage, searchTerm, tagFilter, feedbackFilter, analystFilter]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Handle tag filter
  const handleTagFilterChange = (event) => {
    setTagFilter(event.target.value);
    setPage(0);
  };

  // Handle feedback filter
  const handleFeedbackFilterChange = (event) => {
    setFeedbackFilter(event.target.value);
    setPage(0);
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    setEditFormData({
      tag: item.tag,
      headline: item.headline,
      url: item.url,
      headline_unique_id: item.headline_unique_id,
      feedback: item.feedback,
      user_id: item.user_id || "",
    });
    setEditModalOpen(true);
  };

  // Handle edit form change
  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle edit save
  const handleEditSave = async () => {
    try {
      setLoading(true);
      await updateFeedback(editingItem.id, editFormData);
      setEditModalOpen(false);
      setEditingItem(null);
      setEditFormData({});
      await loadData(); // Reload data
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = (item) => {
    setDeletingItem(item);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await deleteFeedback(deletingItem.id);
      setDeleteModalOpen(false);
      setDeletingItem(null);
      await loadData(); // Reload data
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get domain from URL
  const getDomainFromUrl = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace("www.", "");
    } catch {
      return "Invalid URL";
    }
  };

  if (loading && data.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Filters */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 1,
          flexShrink: 0,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Box display="flex" alignItems="center" gap={0.5}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tag</InputLabel>
              <Select
                value={tagFilter}
                onChange={handleTagFilterChange}
                label="Tag"
              >
                <MenuItem value="">All Tags</MenuItem>
                {uniqueTags.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    {tag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {tagFilter && (
              <Button
                size="small"
                onClick={() => {
                  setTagFilter("");
                  setPage(0);
                }}
                sx={{
                  minWidth: "auto",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  borderRadius: "50%",
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                    color: "text.primary",
                  },
                }}
                title="Clear tag filter"
              >
                <ClearIcon fontSize="small" />
              </Button>
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Analyst</InputLabel>
            <Select
              value={analystFilter}
              onChange={(e) => setAnalystFilter(e.target.value)}
              label="Analyst"
              disabled={analystsLoading}
            >
              <MenuItem value="">All Analysts</MenuItem>
              {analysts.map((analyst) => {
                const analystName =
                  typeof analyst === "object" ? analyst.name : analyst;
                const analystValue =
                  typeof analyst === "object" ? analyst : analyst;
                const analystKey =
                  typeof analyst === "object"
                    ? analyst.id || analyst.name
                    : analyst;
                return (
                  <MenuItem key={analystKey} value={analystValue}>
                    {analystName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Feedback</InputLabel>
            <Select
              value={feedbackFilter}
              onChange={handleFeedbackFilterChange}
              label="Feedback"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Relevant</MenuItem>
              <MenuItem value="false">Not Relevant</MenuItem>
            </Select>
          </FormControl>

          <TextField
            placeholder="Search headlines, tags, or URLs..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
            size="small"
          />
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.grey[100],
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.grey[500],
              },
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Headline</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.tag}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 300,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.headline}
                  >
                    {item.headline}
                  </TableCell>
                  <TableCell>
                    <Link
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        textDecoration: "none",
                        color: theme.palette.primary.main,
                        fontSize: "0.875rem",
                      }}
                    >
                      <OpenInNewIcon fontSize="small" />
                      {getDomainFromUrl(item.url)}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.feedback ? "Relevant" : "Not Relevant"}
                      size="small"
                      color={item.feedback ? "success" : "error"}
                      variant="filled"
                    />
                  </TableCell>
                  <TableCell>{item.user_id || "N/A"}</TableCell>
                  <TableCell>{formatDate(item.feedback_date_time)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(item)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(item)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          sx={{ flexShrink: 0 }}
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Tag"
              value={editFormData.tag || ""}
              onChange={(e) => handleEditFormChange("tag", e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Headline"
              value={editFormData.headline || ""}
              onChange={(e) => handleEditFormChange("headline", e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="URL"
              value={editFormData.url || ""}
              onChange={(e) => handleEditFormChange("url", e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Headline Unique ID"
              value={editFormData.headline_unique_id || ""}
              onChange={(e) =>
                handleEditFormChange("headline_unique_id", e.target.value)
              }
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Feedback</InputLabel>
              <Select
                value={editFormData.feedback}
                onChange={(e) =>
                  handleEditFormChange("feedback", e.target.value)
                }
                label="Feedback"
              >
                <MenuItem value={true}>Relevant</MenuItem>
                <MenuItem value={false}>Not Relevant</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="User ID"
              value={editFormData.user_id || ""}
              onChange={(e) => handleEditFormChange("user_id", e.target.value)}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this feedback entry?
          </Typography>
          {deletingItem && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>ID:</strong> {deletingItem.id}
              </Typography>
              <Typography variant="body2">
                <strong>Tag:</strong> {deletingItem.tag}
              </Typography>
              <Typography variant="body2">
                <strong>Headline:</strong> {deletingItem.headline}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserFeedbackTable;
